import React, { useEffect } from 'react';
import { faBadgeCheck } from '@fortawesome/pro-solid-svg-icons';
import {
  FontAwesomeIcon,
  FontAwesomeIcon as Icon,
} from '@fortawesome/react-fontawesome';
import { Typography } from '@mui/material';
import { ColumnHeader, SimpleTable } from './components/SimpleTable';
import { FiltersBar, SelectLeadsSource } from './CrmFiltersBar';
import TOOLTIPS from './tooltips';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { getLeadsSources } from './CreatorCrm.api';
import { keyBy } from 'lodash';
import { trackEvent } from '@services/tracking';

export type LeadsDto = {
  id: number;
  name: string;
  email: string;
  isEmailVerified: boolean;
  registeredAt: string;
  source: string;
  link: string;
};

function createColumns({
  leadsSources,
}: {
  leadsSources: Array<{ title: string; value: string }>;
}) {
  const leadsMap = keyBy(leadsSources, 'value');

  const columns = [
    {
      title: 'Name',
      field: 'name',
    },
    {
      title: <ColumnHeader label="Email" title={TOOLTIPS.email} />,
      render: (row: LeadsDto) => (
        <div className="flex items-center">
          {row.isEmailVerified ? (
            <Icon icon={faBadgeCheck} className="text-primary mr-2" />
          ) : null}
          <span className="select-all">{row.email}</span>
        </div>
      ),
    },
    {
      title: 'Registered',
      render: (row: LeadsDto) => (
        <div>{new Date(row.registeredAt).toLocaleDateString()}</div>
      ),
    },
    {
      title: <ColumnHeader label="Source" title={TOOLTIPS.source} />,
      render: (row: LeadsDto) => (
        <div>{leadsMap[row.source]?.title || row.source}</div>
      ),
    },
    {
      title: 'Link',
      render: (row: LeadsDto) => (
        <div
          className="w-full cursor-pointer"
          onClick={() => {
            navigator.clipboard.writeText(row.link);
            toast.success('Copied to clipboard');
          }}
        >
          <FontAwesomeIcon icon={faLink} color="#6367EF" />
        </div>
      ),
    },
  ].map((c) => ({ ...c, sorting: false }));

  return columns;
}

export type LeadsTableProps = {
  data: LeadsDto[];
  totalResults: number;
};
export function LeadsTable({ data, totalResults, ...props }: LeadsTableProps) {
  useEffect(() => {
    trackEvent({
      type: 'Viewed Leads Table',
      payload: {},
    });
  }, []);

  const { data: leadsSources } = useQuery(
    ['crm', 'leadsSources'],
    getLeadsSources,
  );
  const columns = createColumns({ leadsSources });

  return (
    <div className="max-w-full">
      <FiltersBar totalResults={totalResults}>
        <SelectLeadsSource />
      </FiltersBar>
      <div className="my-3 text-sm text-[#8A8E99]">
        <Typography>
          Some user details may not appear in this list as they’ve opted to have
          their data hidden per GDPR requirements. This data is solely intended
          for marketing purposes.
        </Typography>
      </div>
      <SimpleTable {...props} columns={columns} data={data} />
    </div>
  );
}
